import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import MarkChatReadRoundedIcon from "@mui/icons-material/MarkChatReadRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router";
import { useSelector } from "react-redux";
import { useMemo } from "react";

import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import {
  DashboardCardEyebrow,
  DashboardCardHeader,
  DashboardCardTitle,
  DashboardCardState,
  DashboardPanelCard,
  DashboardRowLink,
  DashboardRowList,
  DashboardRowMarker,
  DashboardRowText,
} from "_shared/_views/dashboards/dashboard-card";
import { KeptTrail, KeptTrailLead, KeptTrailLine } from "_shared/_views/kept-trail/kept-trail-line";
import { useGetForumsQuery } from "messaging/_api/messaging-api";
import { recordConversationPath } from "messaging/conversations/conversation-links";
import ForumQuery, {
  ForumFilterParameters,
  ForumOrderField,
  ForumOrderParameters,
} from "messaging/forums/_api/queries/forum-query";
import Forum from "messaging/forums/forum";
import { getForumUnreadCounts } from "messaging/forums/_stores/forum-unread-counts-redux-slice";
import type { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import { getForumChatSubtitleParts, getForumChatTitle } from "messaging/forums/forum-chat-display";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useSession } from "users/__session/session-context";
import { useGetProposalsByIdsQuery } from "work/proposals/_api/proposal-api";
import type BarebonesProposalResponse from "work/proposals/_api/responses/barebones-proposal-response";
import { getProposalStatusLabel } from "work/proposals/proposal-status-display";
import { useGetWorkAgreementsByIdsQuery } from "work/work-agreements/_api/work-agreement-api";
import type BarebonesWorkAgreementResponse from "work/work-agreements/_api/responses/barebones-work-agreement-response";
import WorkVersion from "work/work-agreements/versions/work-version";
import { CONVERSATIONS_PATH } from "notifications/_views/communications-list-chips";

const RowCount = styled(Typography)({
  fontWeight: 700,
  lineHeight: 1,
});

/**
 * Where the card's title goes: the Conversations tab, which holds the rows this card is made of.
 *
 * It used to go to `/communications`, and `/communications` is the notification inbox. A reader who
 * followed it from a card headed "22 unread messages" landed on a table of notifications, counted
 * seven rows, and read the difference as the card lying about its own number (#5366). The two
 * counts were never of the same things: a notification is a record that something happened, and an
 * unread message is mail nobody has opened. Marking the notifications read therefore left the
 * card's number exactly where it was, which is the second half of the same report.
 *
 * Since #6719 the tab is `/communications/conversations`, linked straight rather than through the
 * redirect from `/communications/forums`.
 */
export const conversationsRoute = CONVERSATIONS_PATH;
/**
 * Where "+N more conversations" goes: the Conversations tab with its Unread chip on (#6583). The card
 * lists the reader's unread conversations, so its overflow lands on the rest of them, not on every
 * conversation the reader has ever had.
 */
export const unreadConversationsRoute = `${conversationsRoute}?unread=true`;
export const dashboardUnreadConversationLimit = 3;

/** The entity classes whose names this card resolves, as `Topic.entityClass` spells them. */
const proposalEntityClass = "Work.Proposal";
const workAgreementEntityClass = "Work.Agreement";

/**
 * How many unread conversations the card reads.
 *
 * The card asks for the unread ones only, by the same criterion as the Conversations tab's Unread
 * chip, so its "+N more" counts what that filtered tab lists (#6583). A reader with more unread
 * conversations than this sees a header total that reads low rather than one that reads wrong.
 */
export const conversationPageSize = 100;

/**
 * Where a row goes: a work agreement's or a proposal's conversation opens on that record's page, at
 * the oldest unread message (#6585). Any other conversation opens in the chat dialog.
 */
export function forumRoute(forum: Forum): string {
  const forumId = String(forum.id?.value);
  return (
    recordConversationPath({
      forumId,
      topicClass: forum.topic?.entityClass?.value,
      topicId: forum.topic?.entityId?.value,
    }) ?? `${conversationsRoute}/${forumId}`
  );
}

/**
 * How a conversation's topic is named in the maps below: the class and the id together.
 *
 * The class has to be part of the key. A proposal and a work agreement are separate things with
 * separate id spaces, so a key of the id alone would let a row borrow the name of whatever else
 * held that id.
 */
export function forumTopicKey(forum: Forum): string | undefined {
  const entityClass = forum.topic?.entityClass?.value;
  const entityId = forum.topic?.entityId?.value;
  if (!entityClass || !entityId) return undefined;

  return `${entityClass}:${entityId}`;
}

export type ForumTopicIds = {
  proposalIds: string[];
  workAgreementIds: string[];
};

/** The proposals and the work agreements these rows are about, each asked for once. */
export function forumTopicIds(forums: Forum[]): ForumTopicIds {
  const proposalIds = new Set<string>();
  const workAgreementIds = new Set<string>();
  for (const forum of forums) {
    const entityId = forum.topic?.entityId?.value;
    if (!entityId) continue;
    const entityClass = forum.topic?.entityClass?.value;
    if (entityClass === proposalEntityClass) proposalIds.add(entityId);
    if (entityClass === workAgreementEntityClass) workAgreementIds.add(entityId);
  }

  return { proposalIds: [...proposalIds], workAgreementIds: [...workAgreementIds] };
}

export type ForumTopicDetail = {
  /** What the thing the conversation is about is called. */
  name?: string;
  /** The stage that thing is at. Proposals only. */
  status?: string;
  /** Which version of the work it is, "v2" or "v3 (proposed)" (#6809). */
  version?: string;
};

/**
 * What each conversation is about, by topic key.
 *
 * Only proposals carry a stage worth showing. A work agreement has been hired and has nowhere left
 * to be, which is the rule the conversations table already applies (#5129). A proposal the reader
 * may not open comes back as an error with no name and no status, and so contributes nothing.
 */
export function forumTopicDetails(
  proposals: BarebonesProposalResponse[] | undefined,
  workAgreements: BarebonesWorkAgreementResponse[] | undefined,
): Map<string, ForumTopicDetail> {
  const details = new Map<string, ForumTopicDetail>();
  for (const proposal of proposals ?? []) {
    if (!proposal.id) continue;
    details.set(`${proposalEntityClass}:${proposal.id}`, {
      name: proposal.name,
      status: getProposalStatusLabel(proposal.status),
      version: WorkVersion.fromNumbers(proposal)?.label,
    });
  }
  for (const workAgreement of workAgreements ?? []) {
    if (!workAgreement.id) continue;
    details.set(`${workAgreementEntityClass}:${workAgreement.id}`, {
      name: workAgreement.name,
      version: WorkVersion.ofConversation(workAgreement)?.label,
    });
  }

  return details;
}

/** The reader's unread counts by forum id, as the server last sent them after the list loaded. */
type LiveUnreadCounts = Readonly<Record<string, number>>;

/**
 * The forum's unread count: the newest one the server sent, else the one its list loaded with.
 *
 * A read made somewhere else — opening the notification about a system message marks the message
 * read too (#6586) — reaches this page as a realtime count, never as a new list. Without it the
 * card kept counting a message the reader had read until the page was reloaded.
 */
export function unreadCountOf(forum: Forum, liveCounts: LiveUnreadCounts = {}): number {
  const forumId = forum.id?.value;
  return (forumId === undefined ? undefined : liveCounts[forumId]) ?? forum.numUnreadMessages ?? 0;
}

/**
 * The conversations with something unread in them, most unread first.
 *
 * `ForumOrderField` offers `CreatedDate`, `Name`, `EntityId`, `EntityClass` and `Context` — nothing
 * about activity — so "most recently active" is not a sort the server can do and the ordering is
 * done here instead. Unread count is the honest proxy: it is the number the row already shows, so
 * the order and the markers agree with each other.
 */
export function unreadConversations(forums: Forum[], liveCounts: LiveUnreadCounts = {}): Forum[] {
  return forums
    .filter((forum) => Boolean(forum.id) && unreadCountOf(forum, liveCounts) > 0)
    .sort((left, right) => unreadCountOf(right, liveCounts) - unreadCountOf(left, liveCounts));
}

export function totalUnreadMessages(forums: Forum[], liveCounts: LiveUnreadCounts = {}): number {
  return forums.reduce((total, forum) => total + unreadCountOf(forum, liveCounts), 0);
}

const unreadConversationsQuery = new ForumQuery({
  filterParameters: new ForumFilterParameters({ unread: true }),
  orderParameters: new ForumOrderParameters({
    orderBy: ForumOrderField.CreatedDate,
    orderDir: OrderDirection.Descending,
  }),
  paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: conversationPageSize }),
});

/**
 * Who has spoken to you and not been answered.
 *
 * The forums endpoint is already scoped to the calling user and counts each forum's unread
 * messages for them, so there is no "mine" filter to pass and no count to compute here — only the
 * choice of which few to show. This card owns conversations on the dashboard: the recent-activity
 * card excludes message notifications precisely so one unread message is not counted twice on one
 * screen (#5077).
 */
export default function UnreadConversationsCard() {
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  // Communications is permission-gated at the route, so a user without it must not be shown rows
  // leading into it.
  const includesCommunications = sessionPermissions?.includesAccessToCommunications === true;

  // Read afresh every time the dashboard is mounted. Nothing the reader does to a message happens
  // on this page, so the card would otherwise serve whatever it fetched before they left — which is
  // how a reader who went and read their mail came back to the same number (#5366). The cache still
  // answers every render in between; this only says that arriving at the dashboard is a reason to
  // ask again.
  const forumsQuery = useGetForumsQuery(
    { query: unreadConversationsQuery },
    { skip: !includesCommunications, refetchOnMountOrArgChange: true },
  );

  const liveCounts = useSelector(getForumUnreadCounts);
  const unread = useMemo(
    () =>
      unreadConversations(
        (forumsQuery.data ?? []).map((json: ForumJSON) => Forum.fromJSON(json)),
        liveCounts,
      ),
    [forumsQuery.data, liveCounts],
  );
  // Memoized for stability, not for speed: `slice` allocates a new array every render, and the
  // topic ids below are a dependency of the two requests that name them.
  const visible = useMemo(() => unread.slice(0, dashboardUnreadConversationLimit), [unread]);
  const additionalConversationCount = Math.max(0, unread.length - dashboardUnreadConversationLimit);
  const unreadTotal = totalUnreadMessages(unread, liveCounts);

  // Only what the shown rows are about. The card names three conversations however many are
  // unread, so asking about the rest would buy nothing a reader can see.
  const topicIds = useMemo(() => forumTopicIds(visible), [visible]);
  const proposalsQuery = useGetProposalsByIdsQuery(
    { proposalIds: topicIds.proposalIds },
    { skip: topicIds.proposalIds.length === 0 },
  );
  const workAgreementsQuery = useGetWorkAgreementsByIdsQuery(
    { workAgreementIds: topicIds.workAgreementIds },
    { skip: topicIds.workAgreementIds.length === 0 },
  );
  const topicDetails = useMemo(
    () => forumTopicDetails(proposalsQuery.data?.found, workAgreementsQuery.data?.found),
    [proposalsQuery.data, workAgreementsQuery.data],
  );

  if (!includesCommunications) return null;

  return (
    <DashboardPanelCard>
      <DashboardCardHeader>
        <Box>
          <DashboardCardEyebrow>
            <ForumRoundedIcon color="primary" fontSize="small" />
            <Typography color="primary" variant="overline">Conversations</Typography>
          </DashboardCardEyebrow>
          <DashboardCardTitle label="Conversations" to={conversationsRoute}>
            {unreadTotal > 0 ? `${unreadTotal} unread ${unreadTotal === 1 ? "message" : "messages"}` : "Unread messages"}
          </DashboardCardTitle>
        </Box>
      </DashboardCardHeader>
      {forumsQuery.isLoading && (
        <DashboardCardState role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">Loading your conversations…</Typography>
        </DashboardCardState>
      )}
      {!forumsQuery.isLoading && forumsQuery.isError && (
        <DashboardCardState>
          <Typography color="error" variant="body2">Your conversations could not be loaded.</Typography>
        </DashboardCardState>
      )}
      {!forumsQuery.isLoading && !forumsQuery.isError && visible.length === 0 && (
        <DashboardCardState>
          <MarkChatReadRoundedIcon color="success" fontSize="small" />
          <Typography variant="body2">You are caught up.</Typography>
        </DashboardCardState>
      )}
      {!forumsQuery.isLoading && !forumsQuery.isError && visible.length > 0 && (
        <DashboardRowList>
          <Stack spacing={1}>
            {visible.map((forum) => {
              const title = getForumChatTitle(forum, session.user?.fullName);
              const unreadCount = unreadCountOf(forum, liveCounts);
              const topicKey = forumTopicKey(forum);
              const detail = topicKey ? topicDetails.get(topicKey) : undefined;
              // The proposals and agreements come back in their own requests, a moment after the
              // forums, so say the row is waiting rather than show a subtitle that is about to
              // gain a name and read as final (#5129).
              const isAwaitingName =
                (forum.topic?.entityClass?.value === proposalEntityClass && proposalsQuery.isLoading) ||
                (forum.topic?.entityClass?.value === workAgreementEntityClass && workAgreementsQuery.isLoading);
              const topicName = detail?.name ?? (isAwaitingName ? "Loading..." : undefined);
              const subtitle = getForumChatSubtitleParts(
                forum,
                topicName,
                session.user?.fullName,
                detail?.status,
                detail?.version,
              );
              return (
                <DashboardRowLink
                  key={forum.id?.value}
                  to={forumRoute(forum)}
                  aria-label={`${unreadCount} unread in ${title}`}
                >
                  {/* Loud on purpose: somebody has spoken to this user and is waiting to be
                      answered, which is the same kind of thing the queue card holds. */}
                  <DashboardRowMarker aria-hidden="true">
                    <RowCount variant="h6">{unreadCount}</RowCount>
                  </DashboardRowMarker>
                  <DashboardRowText>
                    <Typography noWrap variant="subtitle2">{title}</Typography>
                    <KeptTrailLine color="text.secondary" variant="caption">
                      <KeptTrailLead>{subtitle.lead}</KeptTrailLead>
                      {subtitle.trail && <KeptTrail>{` · ${subtitle.trail}`}</KeptTrail>}
                    </KeptTrailLine>
                  </DashboardRowText>
                  <ArrowForwardRoundedIcon color="action" fontSize="small" />
                </DashboardRowLink>
              );
            })}
          </Stack>
          {additionalConversationCount > 0 && (
            <Button component={RouterLink} to={unreadConversationsRoute} size="small">
              +{additionalConversationCount} more {additionalConversationCount === 1 ? "conversation" : "conversations"}
            </Button>
          )}
        </DashboardRowList>
      )}
    </DashboardPanelCard>
  );
}
